import app, { auth, storage, functions, getFunctionsBaseUrl } from '../firebase';
import { ref, uploadBytes, getDownloadURL, listAll, getMetadata } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';

/** Read a File as base64 string for callable upload. */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = dataUrl && typeof dataUrl === 'string' ? dataUrl.split(',')[1] : null;
      if (base64) resolve(base64);
      else reject(new Error('Could not read file as base64'));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Uploads a DOCX file to Firebase Storage
 * @param {File} file - The file to upload
 * @param {string} userEmail - The email of the user uploading the file
 * @returns {Promise<{path: string, downloadUrl: string}>} - Storage path and download URL
 * @throws {Error} - If validation fails or upload fails
 */
export const uploadDocx = async (file, userEmail) => {
  // Validation: Check if file exists
  if (!file) {
    throw new Error('No file selected');
  }

  // Validation: Check file extension and type (.docx or .txt)
  const fileExtension = file.name.toLowerCase().split('.').pop();
  const allowedExtensions = ['docx', 'txt'];
  if (!allowedExtensions.includes(fileExtension)) {
    throw new Error('Invalid file extension. Only .docx and .txt files are allowed');
  }
  const allowedMime = [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
  ];
  const validMime =
    allowedMime.includes(file.type) ||
    (fileExtension === 'txt' && (file.type === 'text/plain' || file.type === ''));
  if (!validMime) {
    throw new Error('Invalid file type. Only .docx and .txt files are allowed');
  }

  // Validation: Check file size (10MB limit)
  const maxSize = 10 * 1024 * 1024; // 10MB in bytes
  if (file.size > maxSize) {
    throw new Error('File too large. Maximum size is 10MB');
  }

  try {
    // Sanitize filename
    const sanitizedFileName = sanitizeFileName(file.name);
    
    // Generate timestamp
    const timestamp = Date.now();
    
    // Create storage path
    const fileName = `${timestamp}_${sanitizedFileName}`;
    const storagePath = `quizzes/${fileName}`;
    
    // Create storage reference
    const storageRef = ref(storage, storagePath);
    
    // Upload file with metadata (ensure .txt has text/plain)
    const contentType =
      file.type ||
      (fileExtension === 'txt' ? 'text/plain' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
    const metadata = {
      contentType,
      customMetadata: {
        'uploadedBy': userEmail || 'unknown',
        'uploadTime': new Date().toISOString()
      }
    };
    
    const snapshot = await uploadBytes(storageRef, file, metadata);
    
    // Get download URL
    const downloadUrl = await getDownloadURL(snapshot.ref);
    
    console.log('Upload successful:', {
      path: storagePath,
      downloadUrl: downloadUrl,
      size: file.size,
      type: file.type
    });
    
    return {
      path: storagePath,
      downloadUrl: downloadUrl
    };
  } catch (error) {
    console.error('Upload error:', error);
    if (error && typeof error === 'object') {
      console.error('Upload error code/message:', error.code, error.message);
    }

    // Force-save for admin: when client Storage rules deny, try server-side upload (admin-only callable or HTTP with CORS)
    if (error.code === 'storage/unauthorized' && app && functions) {
      const contentType =
        file.type ||
        (fileExtension === 'txt' ? 'text/plain' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
      const payload = { fileBase64: null, fileName: file.name, contentType };
      try {
        payload.fileBase64 = await fileToBase64(file);
      } catch (e) {
        console.warn('Admin upload fallback: could not read file as base64', e?.message);
      }
      if (payload.fileBase64) {
        try {
          const uploadQuizFile = httpsCallable(functions, 'uploadQuizFile');
          const res = await uploadQuizFile(payload);
          const data = res?.data;
          if (data?.path && data?.downloadUrl) {
            return { path: data.path, downloadUrl: data.downloadUrl };
          }
        } catch (callableErr) {
          const baseUrl = getFunctionsBaseUrl();
          const token = auth?.currentUser ? await auth.currentUser.getIdToken() : null;
          if (baseUrl && token) {
            try {
              const r = await fetch(`${baseUrl}/uploadQuizFileHttp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ data: payload }),
              });
              if (!r.ok) {
                const errBody = await r.json().catch(() => ({}));
                throw new Error(errBody?.error?.message || r.statusText);
              }
              const json = await r.json();
              const data = json?.result;
              if (data?.path && data?.downloadUrl) {
                return { path: data.path, downloadUrl: data.downloadUrl };
              }
            } catch (httpErr) {
              console.warn('Admin upload fallback failed:', callableErr?.message || callableErr, '; HTTP:', httpErr?.message || httpErr);
            }
          } else {
            console.warn('Admin upload fallback failed:', callableErr?.message || callableErr);
          }
        }
      }
    }

    // Provide user-friendly error messages
    if (error.code === 'storage/unauthorized') {
      throw new Error('Upload denied. If you are an admin, ensure your email is listed in Storage rules and your email is verified (contact your administrator).');
    } else if (error.code === 'storage/canceled') {
      throw new Error('Upload was canceled');
    } else if (error.code === 'storage/unknown') {
      throw new Error('An unknown error occurred during upload. Please check your internet connection and try again.');
    } else if (error.code === 'storage/quota-exceeded') {
      throw new Error('Storage quota exceeded. Please contact the administrator.');
    } else if (error.code === 'storage/unauthenticated') {
      throw new Error('You must be signed in to upload files. Please sign in and try again.');
    } else {
      throw new Error(`Failed to upload file: ${error.message || 'Please try again.'}`);
    }
  }
};

/**
 * Sanitizes filename by removing special characters and replacing spaces
 * @param {string} fileName - Original filename
 * @returns {string} - Sanitized filename
 */
const sanitizeFileName = (fileName) => {
  // Remove special characters except letters, numbers, spaces, dots, and hyphens
  // Replace spaces with underscores
  // Limit length to prevent issues
  return fileName
    .replace(/[^a-zA-Z0-9\s.-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 100); // Limit to 100 characters
};

const ALLOWED_EXTENSIONS = ['docx', 'txt'];
const ALLOWED_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];

/**
 * Validates a file for quiz upload (.docx or .txt)
 * @param {File} file - The file to validate
 * @returns {string|null} - Error message if invalid, null if valid
 */
export const validateDocxFile = (file) => {
  if (!file) {
    return 'No file selected';
  }

  const fileExtension = file.name.toLowerCase().split('.').pop();
  if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
    return 'Invalid file extension. Only .docx and .txt files are allowed';
  }

  // For .txt, accept text/plain or empty (some OS don't set type)
  const validType =
    ALLOWED_MIME_TYPES.includes(file.type) ||
    (fileExtension === 'txt' && (file.type === 'text/plain' || file.type === ''));
  if (!validType) {
    return 'Invalid file type. Only .docx and .txt files are allowed';
  }

  // Check file size (10MB limit)
  const maxSize = 10 * 1024 * 1024; // 10MB in bytes
  if (file.size > maxSize) {
    return 'File too large. Maximum size is 10MB';
  }

  return null; // Valid file
};

/**
 * List all quiz files from Firebase Storage (quizzes/ folder).
 * Returns array of { path, downloadUrl, fileName, uploadedBy }.
 */
export const listQuizFilesFromStorage = async () => {
  try {
    const listRef = ref(storage, 'quizzes');
    const result = await listAll(listRef);
    const files = [];
    for (const itemRef of result.items) {
      const [downloadUrl, metadata] = await Promise.all([
        getDownloadURL(itemRef),
        getMetadata(itemRef).catch(() => null),
      ]);
      const path = itemRef.fullPath;
      const fileName = path.split('/').pop() || 'quiz.docx';
      const uploadedBy = metadata?.customMetadata?.uploadedBy || 'unknown';
      files.push({ path, downloadUrl, fileName, uploadedBy });
    }
    return files;
  } catch (err) {
    console.error('List Storage files error:', err);
    return [];
  }
};
